'use client';
import React, { useEffect, useRef } from 'react';
import type { FC, ReactNode, SyntheticEvent } from 'react';

import { cn } from '@wwe-portal/ui/lib/lib';

import { ClickableDiv } from '../button';
import { Each } from '../utils';

export const NavigationItem: FC<{
  id: string;
  activeId: string | undefined;
  onClick?: (event: SyntheticEvent) => void;
  step?: number;
  className?: string;
  icon?: ReactNode;
  label: string;
}> = ({ activeId, onClick, icon, label, id, step, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isActive = activeId === id;

  useEffect(() => {
    if (ref.current == null || !isActive || step == null) return;
    const offsetWidth = ref.current.getBoundingClientRect().width;
    ref.current.parentElement?.scrollTo({
      behavior: 'smooth',
      left: offsetWidth * step - offsetWidth / 2,
    });
  }, [isActive, step]);

  return (
    <ClickableDiv
      className={cn(
        'bg-back200 text-textColor3 hover:bg-bgColor4 hover:text-textColor4 w-full min-w-[110x] cursor-pointer text-nowrap rounded-md p-1.5 px-5 text-center font-semibold transition-all duration-300 ease-linear md:py-2',
        {
          'bg-bgColor4 text-textColor4': isActive,
        },
        className
      )}
      data-id={id}
      onClick={onClick}
      ref={ref}
      tabIndex={-1}
    >
      <div className="flex items-center justify-center gap-1">
        {icon}
        <span>{label}</span>
      </div>
    </ClickableDiv>
  );
};

export interface NavigationItemData {
  icon?: React.ReactNode;
  id: string;
  label: string;
}
export interface NavigationContainerProps {
  activeId: string | undefined;
  items: NavigationItemData[];
  onClick?: (item: NavigationItemData) => void;
  itemClassName?: string;
  className?: string;
}

export const NavigationMenu: FC<NavigationContainerProps> = ({
  items,
  onClick,
  activeId,
  className,
  itemClassName,
}) => {
  return (
    <div
      className={cn(
        'no-scrollbar flex w-auto flex-row flex-nowrap gap-5 overflow-auto text-xs',
        className
      )}
    >
      <Each
        data={items}
        render={(item, index) => (
          <NavigationItem
            activeId={activeId}
            className={itemClassName}
            icon={item.icon}
            id={item.id}
            key={item.id}
            label={item.label}
            onClick={() => onClick?.(item)}
            step={index}
          />
        )}
      />
    </div>
  );
};
